import { ReactNode, useState } from 'react';

import { ButtonBase } from '@mui/material';

import { Box, CHECKBOX_CHECK_PATH, useTheme } from '@sbv-clients/ui-shared';

import { NavIcon } from './nav-icons';

export type FacilitySwitcherOption = {
  value?: string | number;
  label?: ReactNode;
};

export const FacilitySwitcher = ({
  label,
  current,
  options,
  selected,
  onSelect,
  disabled,
}: {
  label: string;
  current: ReactNode;
  options: FacilitySwitcherOption[];
  selected: string;
  onSelect: (value: string) => void;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const canSwitch = options.length > 1;

  return (
    <>
      <ButtonBase
        onClick={() => canSwitch && setOpen((o) => !o)}
        disableRipple={!canSwitch}
        aria-expanded={canSwitch ? open : undefined}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 10px',
          mb: '6px',
          borderRadius: '10px',
          textAlign: 'left',
          cursor: canSwitch ? 'pointer' : 'default',
          background: open
            ? theme.custom?.shellProfileOpenBg
            : theme.custom?.shellProfileBg,
        }}
      >
        <NavIcon
          name="building"
          size={18}
          sx={{ color: theme.custom?.brandIndigo }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="small"
            sx={{
              display: 'block',
              fontSize: '11.5px',
              lineHeight: '14px',
              color: theme.custom?.shellMutedText,
            }}
          >
            {label}
          </Box>
          <Box
            component="b"
            sx={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: '18px',
              color: theme.custom?.brandNavy,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {current}
          </Box>
        </Box>
        {canSwitch && (
          <NavIcon
            name="chevron"
            size={14}
            sx={{
              color: theme.custom?.shellNavChevron,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          />
        )}
      </ButtonBase>
      {open && (
        <Box
          sx={{
            padding: '4px 0 4px 30px',
            mb: '6px',
            display: 'grid',
            gap: '1px',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {options.map((option) => {
            const value = String(option.value ?? '');
            const on = value === selected;
            return (
              <ButtonBase
                key={value}
                disabled={disabled}
                onClick={() => onSelect(value)}
                sx={{
                  width: '100%',
                  minHeight: 35,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  textAlign: 'left',
                  fontSize: '13.5px',
                  fontWeight: on ? 600 : 400,
                  color: on
                    ? theme.custom?.brandNavy
                    : theme.custom?.shellMenuItemText,
                  background: on ? theme.custom?.shellRoleBg : 'none',
                  '&:hover': {
                    background: on
                      ? theme.custom?.shellRoleBg
                      : theme.custom?.shellMenuItemHoverBg,
                  },
                }}
              >
                {option.label}
                {on && (
                  <Box
                    component="svg"
                    viewBox="0 0 24 24"
                    aria-hidden
                    sx={{
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      fill: theme.custom?.brandIndigo,
                    }}
                  >
                    <path d={CHECKBOX_CHECK_PATH} />
                  </Box>
                )}
              </ButtonBase>
            );
          })}
        </Box>
      )}
    </>
  );
};
